import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, catchError, filter, finalize, switchMap, take, tap, timer } from 'rxjs';
import { DemoAccountApiService, DemoAccountResetStatus } from '@shared/services';
import { ButtonV3Component } from '@gea/digital-ui-lib';
import { SnackbarV3Service } from '@gea/digital-ui-lib';

import { ChangeDetectionStrategy } from '@angular/core';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonV3Component],
  template: `
    <geaui-button-v3 (click)="resetData()" [disabled]="isLoading" [sending]="isLoading">Reset Demo data</geaui-button-v3>
  `,
  selector: 'gea-hrt-reset-demo-account',
})
export class ResetDemoAccountComponent {
  isLoading = false;
  private readonly service = inject(DemoAccountApiService);
  private readonly snackBar = inject(SnackbarV3Service);
  private readonly destroyRef = inject(DestroyRef);

  resetData(): void {
    this.isLoading = true;
    this.service
      .createData()
      .pipe(
        tap(() => this.showStarted('Demo account reset started. This can take a few minutes.')),
        switchMap((queuedStatus) =>
          timer(0, 5000).pipe(
            switchMap(() => this.service.getStatus()),
            filter((status) => this.isSameOperation(status, queuedStatus)),
            filter((status) => this.isTerminalStatus(status)),
            take(1)
          )
        ),
        takeUntilDestroyed(this.destroyRef),
        catchError(() => {
          this.showError('Unable to start or monitor the demo account reset.');
          return EMPTY;
        }),
        finalize(() => (this.isLoading = false))
      )
      .subscribe((status) => {
        if (this.getStatusState(status) === 'completed') {
          this.showSuccess('Demo account reset completed. Page will refresh automatically.');
          setTimeout(() => window.location.reload(), 1200);
          return;
        }

        this.showError(status.error ? `Demo account reset failed: ${status.error}` : 'Demo account reset failed.');
      });
  }

  private isSameOperation(status: DemoAccountResetStatus, queuedStatus: DemoAccountResetStatus): boolean {
    if (!queuedStatus.operationId) {
      return true;
    }

    return status.operationId === queuedStatus.operationId;
  }

  private isTerminalStatus(status: DemoAccountResetStatus): boolean {
    const state = this.getStatusState(status);
    return state === 'completed' || state === 'failed';
  }

  private getStatusState(status: DemoAccountResetStatus): string {
    return (status.state ?? '').trim().toLowerCase();
  }

  private showSuccess(detail: string): void {
    this.snackBar.add({
      severity: 'success',
      summary: 'Reset completed',
      detail,
    });
  }

  private showError(detail: string): void {
    this.snackBar.add({
      severity: 'error',
      summary: 'Reset failed',
      detail,
    });
  }

  private showStarted(detail: string): void {
    this.snackBar.add({
      severity: 'success',
      summary: 'Reset in progress',
      detail,
    });
  }
}
